import { useCallback, useState } from 'react'
import { Platform } from 'react-native'

import { useRouter } from 'expo-router'
import { useConvex } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { useStore } from '../models'
import { events } from '../models/analytics/analytics-events'
import { GameModeTag, TournamentAccessControl, TournamentDifficulty } from '../models/api-store'
import { extendLogger, LoggerContexts } from '../utils/logger'
import { InputEditUserGameSchedule } from '../models/api-store/RootStore.base'
import { useIsConvexEnabled } from '@/hooks/convex/useIsConvexEnabled'

const logger = extendLogger(LoggerContexts.LobbyScreen)

interface CreateGameConfig {
  gameKind: GameModeTag
  accessControl?: TournamentAccessControl
  scheduleForLater?: boolean
  scheduleForLaterAt?: Date
  /** When set, creates a Convex private game linked to this lounge and joins the host. */
  loungeId?: Id<'lounges'>
}

interface EditScheduleConfig {
  hash: string
  payload: InputEditUserGameSchedule
}

interface TournamentConfig {
  tournamentName: string
  tournamentBlindsLength: string
  tournamentStartingChips: string
  tournamentActionTime: string
  tournamentDifficulty: TournamentDifficulty
  accessControl: TournamentAccessControl
}

interface CreateGameOptions {
  /** When true, creator joins as teacher (observer) in the teacher seat instead of as a player. */
  asTeacher?: boolean
}

export const useCreateGame = () => {
  const router = useRouter()
  const convex = useConvex()
  const convexConfig = useIsConvexEnabled()

  const [loading, setLoading] = useState(false)
  const [convexGameCode, setConvexGameCode] = useState<string | null>(null)
  const {
    analytics: { setEvent },
    communication: { channel, clearChannel },
    game: {
      clearGame,
      createTournament,
      game,
      playKind,
      makeNewGame,
      createUserGameSchedule,
      editUserGameSchedule,
      deleteUserGameSchedule,
      getYourScheduledGames
    },
    session: { logout, user },
    share: { info, promptGameShare, setShare }
  } = useStore()

  const redirectToScreen = useCallback(
    ({ gameKind, accessControl, scheduleForLater }: CreateGameConfig, convexGameId?: string) => {
      const isTournament = [GameModeTag.tournament, GameModeTag.practiceTournament].includes(gameKind)
      const isThreeCard = gameKind === GameModeTag.threeCard

      if (scheduleForLater) {
        router.dismissTo('/game/create/schedule-confirm')
      } else if (convexGameId) {
        // Convex game - navigate to new game route
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        router.dismissTo({ pathname: '/game/[gameId]', params: { gameId: convexGameId } } as any)
      } else if (isTournament && accessControl !== TournamentAccessControl.private) {
        router.dismissTo(`/lobby/active-games?accessControl=${accessControl}`)
      } else if (isThreeCard) {
        router.dismissTo('/game/three-card')
      } else {
        router.dismissTo('/game')
      }
    },
    []
  )

  const createGame = useCallback(
    async (
      gameConfig: CreateGameConfig,
      tournamentConfig?: TournamentConfig,
      options?: CreateGameOptions
    ): Promise<{ gameId: Id<'games'> } | undefined> => {
      const { accessControl, gameKind, scheduleForLater, scheduleForLaterAt, loungeId } = gameConfig
      if (!gameKind) {
        throw new Error('Game mode must be provided')
      }

      const isTournament = [GameModeTag.tournament, GameModeTag.practiceTournament].includes(gameKind)
      if (isTournament && !tournamentConfig && !loungeId) {
        throw new Error('Tournament mode must provide tournamentConfig')
      }

      const isQuickGame = gameKind === GameModeTag.default
      const useConvex = (isQuickGame && convexConfig.enabled) || !!loungeId

      const showShare =
        gameKind === GameModeTag.default ||
        (gameKind === GameModeTag.tournament && accessControl === TournamentAccessControl.private)

      try {
        setLoading(true)
        clearChannel()
        clearGame()
        const hasChannel = !!channel?.channelName
        logger.info('onShare GameScreen.useEffect hasChannel', hasChannel)

        const alreadyHaveGameInfo = Boolean(game)

        if (!alreadyHaveGameInfo) {
          logger.info('onShare GameScreen.useEffect creating channel and game...', user?._id)

          // Use Convex for private quick games when enabled, or for lounge-linked games
          if (useConvex) {
            const userId = user?.auth0Id
            const userName = user?.name || 'Player'
            const mongoUserId = user?._id.toString()

            if (!userId || !mongoUserId) {
              throw new Error('User not authenticated')
            }

            // Derive game params: lounge games use the tournament form values; quick games use defaults.
            const tournamentBlinds = tournamentConfig?.tournamentBlindsLength?.replace(' min', '') ?? ''
            const tournamentAction = tournamentConfig?.tournamentActionTime?.replace(' sec', '') ?? ''
            const tournamentStartingChipsStr = tournamentConfig?.tournamentStartingChips ?? ''
            const startingChipsNum = tournamentStartingChipsStr ? Number(tournamentStartingChipsStr) : undefined
            const blindTimerMs = tournamentBlinds ? Number(tournamentBlinds) * 60 * 1000 : undefined
            const turnDurationMs = tournamentAction ? Number(tournamentAction) * 1000 : undefined

            // Create private game in Convex (quick game by default, lounge-linked when loungeId present)
            const result = await convex.mutation(api.games.mutations.createPrivateGame, {
              name: tournamentConfig?.tournamentName || `${userName}'s Game`,
              userId,
              ...(loungeId ? { loungeId } : {}),
              ...(startingChipsNum !== undefined ? { startingChips: startingChipsNum } : {}),
              ...(blindTimerMs !== undefined ? { timeBetweenBlindsIncrease: blindTimerMs } : {}),
              ...(turnDurationMs !== undefined ? { turnDuration: turnDurationMs } : {})
            })

            const { gameId, code } = result
            setConvexGameCode(code)

            // Teacher-observer: sit in the teacher seat without taking a player slot
            const joinAsTeacherSeat = options?.asTeacher ?? playKind === 'observer'
            if (joinAsTeacherSeat) {
              await convex.mutation(api.games.teacherSeat.joinAsTeacher, {
                gameId,
                userId,
                name: userName
              })
            } else {
              // Creator explicitly chose to play — skip the teacher→observer redirect.
              await convex.mutation(api.games.mutations.joinGame, {
                gameId,
                userId,
                mongoUserId,
                name: userName,
                asPlayer: true
              })
            }

            setEvent(events.game.game_created, {
              playerName: userName,
              joinCode: code,
              userId: mongoUserId,
              loungeId: loungeId ?? undefined
            })

            if (!loungeId) {
              setShare(code)
              redirectToScreen(gameConfig, gameId)
            }

            // Note: For Convex quick games and lounge games, we don't auto-open native share.
            // The WaitingForPlayersSheet in the game screen handles sharing.
            // E.g. the caller (screen) handles announce + navigation.

            logger.info('Convex private game created:', gameId, code)
            return { gameId }
          } else if (isTournament) {
            if (!tournamentConfig) {
              throw new Error('Missing tournament config')
            }

            const {
              tournamentName,
              tournamentBlindsLength,
              tournamentStartingChips,
              tournamentActionTime,
              tournamentDifficulty,
              accessControl
            } = tournamentConfig

            const actionTime = tournamentActionTime.replace(' sec', '')
            const tournamentBlinds = tournamentBlindsLength.replace(' min', '')

            if (scheduleForLater && scheduleForLaterAt) {
              await createUserGameSchedule(
                tournamentName,
                gameKind,
                tournamentBlinds.length > 0 ? Number(tournamentBlinds) : undefined,
                actionTime.length > 0 ? Number(actionTime) : undefined,
                tournamentStartingChips.length > 0 ? Number(tournamentStartingChips) : undefined,
                tournamentDifficulty,
                accessControl,
                scheduleForLaterAt
              )
              setEvent(events.game.scheduled_game_created, {
                date: scheduleForLaterAt.toDateString(),
                time: scheduleForLaterAt.toTimeString(),
                game_name: tournamentName
              })
            } else {
              await createTournament(
                tournamentName,
                gameKind,
                tournamentBlinds.length > 0 ? Number(tournamentBlinds) : undefined,
                actionTime.length > 0 ? Number(actionTime) : undefined,
                tournamentStartingChips.length > 0 ? Number(tournamentStartingChips) : undefined,
                tournamentDifficulty,
                accessControl
              )
            }

            const hash = info?.hash

            if (hash && Platform.OS !== 'web') {
              setEvent(events.game.game_created, {
                playerName: user?.name,
                joinCode: hash,
                userId: user?._id
              })
            }

            redirectToScreen(gameConfig)

            if (showShare) {
              await promptGameShare()
            }
          } else {
            await makeNewGame(gameKind)

            const hash = info?.hash

            if (hash && Platform.OS !== 'web') {
              setEvent(events.game.game_created, {
                playerName: user?.name,
                joinCode: hash,
                userId: user?._id
              })
            }

            redirectToScreen(gameConfig)

            if (showShare) {
              await promptGameShare()
            }
          }

          logger.info('onShare GameScreen.useEffect channel and game created')
        }
      } catch (error) {
        logger.error('onShare', error)
        if (error instanceof Error && error.message.includes('Unauthorized user')) {
          logout()
        }
        throw error
      } finally {
        setLoading(false)
      }
      return undefined
    },
    [
      redirectToScreen,
      channel,
      clearChannel,
      createTournament,
      clearGame,
      game,
      playKind,
      info,
      logout,
      makeNewGame,
      promptGameShare,
      setEvent,
      createUserGameSchedule,
      user,
      convex,
      convexConfig,
      setShare
    ]
  )

  const editScheduledGame = useCallback(
    async (input: EditScheduleConfig) => {
      try {
        setLoading(true)
        await editUserGameSchedule(input.hash, input.payload)
        await getYourScheduledGames()
        router.navigate('/game/create/schedule-confirm?shouldManage=true')
      } catch (error) {
        logger.error('editScheduledGame', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [editUserGameSchedule, getYourScheduledGames]
  )

  return {
    loading,
    createGame,
    editScheduledGame,
    setLoading,
    convexGameCode
  }
}
